// Renderer-side SSE consumer for the SDL-backed gamepad driver running
// in the Electron main process. Connects once at app start, re-emits
// every event it receives onto hidGamepadBus. The same bus the Web
// HID fallback publishes to and that gamepadPoll forwards into every
// downstream consumer (focus bridge, GameBoard, VirtualKeyboard, the
// rebind capture UI).
//
// There is no state here. The main process owns the controllers; we
// are a dumb pipe. If the SSE stream closes (e.g. server restart in
// dev), we retry with exponential backoff up to a cap.

import { hidGamepadBus } from './hidGamepadBridge';
import { getLocalApiOrigin } from '../localApi';

const MIN_RETRY_MS = 500;
const MAX_RETRY_MS = 10000;

// Toggle via localStorage.setItem('vk-debug-controller', '1') to see
// every event in the DevTools console. Off by default to keep the log
// clean for normal use.
const DEBUG = (() => {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem('vk-debug-controller') === '1'; }
  catch (_) { return false; }
})();

let eventSource = null;
let retryTimer = null;
let retryDelay = MIN_RETRY_MS;
let lastStatus = { available: false, controllers: [] };
const statusListeners = new Set();

function emit(name, detail) {
  hidGamepadBus.dispatchEvent(new CustomEvent(name, { detail }));
}

function setStatus(next) {
  lastStatus = next || lastStatus;
  for (const fn of statusListeners) fn(lastStatus);
}

function openStream() {
  if (eventSource) return;
  const origin = getLocalApiOrigin();
  const url = `${origin}/api/gamepad/events`;
  let src;
  try { src = new EventSource(url); }
  catch (err) {
    console.warn('[sdlGamepadClient] EventSource not available:', err?.message || err);
    return;
  }
  eventSource = src;

  src.onmessage = (e) => {
    // EventSource collapses to onmessage when no `event:` field is
    // present; our server sends JSON-per-line under default "message".
    let msg;
    try { msg = JSON.parse(e.data); }
    catch (_) { return; }
    if (!msg || typeof msg !== 'object') return;
    switch (msg.type) {
      case 'hello':
        if (msg.status) setStatus(msg.status);
        retryDelay = MIN_RETRY_MS;
        break;
      case 'device':
        // Refresh status snapshot when a controller appears or leaves.
        fetchStatus();
        break;
      case 'button-down':
      case 'button-up':
      case 'axis':
        if (DEBUG) console.log('[sdlClient]', msg.type, msg.detail);
        emit(msg.type, msg.detail);
        break;
      default: break;
    }
  };

  src.onerror = () => {
    try { src.close(); } catch (_) {}
    eventSource = null;
    // Exponential backoff so a failing server doesn't spam reconnects.
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      openStream();
    }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
  };
}

async function fetchStatus() {
  try {
    const res = await fetch(`${getLocalApiOrigin()}/api/gamepad/status`);
    if (!res.ok) return;
    const status = await res.json();
    setStatus(status);
  } catch (_) {}
}

export function startSdlGamepadClient() {
  openStream();
  fetchStatus();
}

export function stopSdlGamepadClient() {
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  if (eventSource) { try { eventSource.close(); } catch (_) {} eventSource = null; }
}

export function getSdlGamepadStatus() { return lastStatus; }
export function subscribeSdlGamepadStatus(fn) {
  statusListeners.add(fn);
  fn(lastStatus);
  return () => statusListeners.delete(fn);
}
